import { useState, useEffect } from "react";
import { api, authHeaders } from "../api";

function Toggle({ label, description, value, onChange, danger }) {
  const c = danger ? "#E03030" : "#00E5C0";
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "18px 20px", borderBottom: "1px solid var(--border2)",
    }}>
      <div>
        <div style={{ color: "var(--text)", fontSize: 13, fontWeight: 600 }}>{label}</div>
        <div style={{ color: "#555E6E", fontSize: 10, marginTop: 4 }}>{description}</div>
      </div>
      <button
        onClick={() => onChange(!value)}
        style={{
          width: 48, height: 26, borderRadius: 13, border: "none",
          background: value ? c : "var(--input-bg)",
          cursor: "pointer", position: "relative", transition: "background .2s",
          flexShrink: 0,
        }}
      >
        <span style={{
          position: "absolute", top: 3,
          left: value ? 25 : 3,
          width: 20, height: 20, borderRadius: "50%",
          background: value ? "#0D0F14" : "#2A3040",
          transition: "left .2s",
          display: "block",
        }} />
      </button>
    </div>
  );
}

export default function ConfigView({ theme, onToggleTheme }) {
  const [cfg, setCfg]           = useState(null);
  const [wallet, setWallet]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [stakeInput, setStakeInput]   = useState("");
  const [slInput, setSlInput]         = useState("");
  const [trailInput, setTrailInput]   = useState("");
  const [tpInput, setTpInput]         = useState("");
  const [maxPosInput, setMaxPosInput] = useState("");

  useEffect(() => {
    fetch("/api/config", { headers: authHeaders() }).then(r => r.json()).then(data => {
      setCfg(data);
      setStakeInput(String(data.stake_usdt ?? 10));
      setSlInput(String(data.stop_loss_pct ?? 5));
      setTrailInput(String(data.trailing_pct ?? 5));
      setTpInput(String(data.take_profit_pct ?? 15));
      setMaxPosInput(String(data.max_positions ?? 5));
    });
    fetch("/api/wallet", { headers: authHeaders() }).then(r => r.json()).then(setWallet);
  }, []);

  const update = async (key, val) => {
    const next = { ...cfg, [key]: val };
    setCfg(next);
    setSaving(true);
    setSaved(false);
    await fetch("/api/config", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ [key]: val }),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (!cfg) return (
    <div style={{ textAlign: "center", padding: 60, color: "#2A3040" }}>Chargement...</div>
  );

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", animation: "fadeIn .3s ease" }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 20, gap: 12 }}>
        <span style={{ fontSize: 14, color: "#E0E4F0", fontWeight: 700 }}>Configuration</span>
        {saving && <span style={{ fontSize: 9, color: "#555E6E" }}>Sauvegarde...</span>}
        {saved  && <span style={{ fontSize: 9, color: "#00E5C0" }}>✓ Sauvegardé</span>}
      </div>

      <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
        {/* Section alertes */}
        <div style={{ padding: "12px 20px", background: "var(--bg3)",
                      fontSize: 8, color: "var(--text-dimmer)", letterSpacing: 2 }}>
          ALERTES
        </div>

        <Toggle
          label="Alertes WhatsApp"
          description="Envoie un message WhatsApp dès qu'un signal BUY est détecté"
          value={cfg.whatsapp_enabled}
          onChange={v => update("whatsapp_enabled", v)}
        />

        {/* Section trading */}
        <div style={{ padding: "12px 20px", background: "var(--bg3)",
                      fontSize: 8, color: "var(--text-dimmer)", letterSpacing: 2 }}>
          TRADING
        </div>

        <Toggle
          label="Mode réel — Achat automatique"
          description="Active l'achat automatique sur DEX (Phase 3). Désactivé par défaut."
          value={cfg.auto_buy_enabled}
          onChange={v => update("auto_buy_enabled", v)}
          danger
        />

        {/* Stake amount */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 20px", borderBottom: "1px solid var(--border2)",
        }}>
          <div>
            <div style={{ color: "#E0E4F0", fontSize: 13, fontWeight: 600 }}>Montant par achat (USDT)</div>
            <div style={{ color: "#555E6E", fontSize: 10, marginTop: 4 }}>Mise engagée à chaque signal BUY</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="number"
              min="1"
              step="1"
              value={stakeInput}
              onChange={e => setStakeInput(e.target.value)}
              style={{
                width: 72, padding: "6px 10px", borderRadius: 6,
                background: "var(--input-bg)", border: "1px solid var(--text-dimmer)",
                color: "#E0E4F0", fontSize: 13, textAlign: "right",
                outline: "none",
              }}
            />
            <span style={{ color: "#555E6E", fontSize: 11 }}>USDT</span>
            <button
              onClick={() => { const v = parseFloat(stakeInput); if (v > 0) update("stake_usdt", v); }}
              style={{
                padding: "6px 12px", borderRadius: 6, border: "none",
                background: "#00E5C020", color: "#00E5C0",
                fontSize: 10, cursor: "pointer", fontWeight: 600,
              }}
            >
              Appliquer
            </button>
          </div>
        </div>

        {/* Stop Loss */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 20px", borderBottom: "1px solid var(--border2)",
        }}>
          <div>
            <div style={{ color: "#E0E4F0", fontSize: 13, fontWeight: 600 }}>Stop Loss (%)</div>
            <div style={{ color: "#555E6E", fontSize: 10, marginTop: 4 }}>Vente automatique si P&L ≤ −X%</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="number" min="0.1" step="0.1"
              value={slInput}
              onChange={e => setSlInput(e.target.value)}
              style={{
                width: 72, padding: "6px 10px", borderRadius: 6,
                background: "var(--input-bg)", border: "1px solid var(--text-dimmer)",
                color: "#E03030", fontSize: 13, textAlign: "right", outline: "none",
              }}
            />
            <span style={{ color: "#555E6E", fontSize: 11 }}>%</span>
            <button
              onClick={() => { const v = parseFloat(slInput); if (v > 0) update("stop_loss_pct", v); }}
              style={{
                padding: "6px 12px", borderRadius: 6, border: "none",
                background: "#E0303020", color: "#E03030",
                fontSize: 10, cursor: "pointer", fontWeight: 600,
              }}
            >Appliquer</button>
          </div>
        </div>

        {/* Trailing Stop */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 20px", borderBottom: "1px solid var(--border2)",
        }}>
          <div>
            <div style={{ color: "#E0E4F0", fontSize: 13, fontWeight: 600 }}>Trailing Stop (%)</div>
            <div style={{ color: "#555E6E", fontSize: 10, marginTop: 4 }}>Vente si recul ≥ X% depuis le pic (après profit)</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="number" min="0.1" step="0.1"
              value={trailInput}
              onChange={e => setTrailInput(e.target.value)}
              style={{
                width: 72, padding: "6px 10px", borderRadius: 6,
                background: "var(--input-bg)", border: "1px solid var(--text-dimmer)",
                color: "#E5A000", fontSize: 13, textAlign: "right", outline: "none",
              }}
            />
            <span style={{ color: "#555E6E", fontSize: 11 }}>%</span>
            <button
              onClick={() => { const v = parseFloat(trailInput); if (v > 0) update("trailing_pct", v); }}
              style={{
                padding: "6px 12px", borderRadius: 6, border: "none",
                background: "#E5A00020", color: "#E5A000",
                fontSize: 10, cursor: "pointer", fontWeight: 600,
              }}
            >Appliquer</button>
          </div>
        </div>

        {/* Take Profit partiel */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 20px", borderBottom: "1px solid var(--border2)",
        }}>
          <div>
            <div style={{ color: "var(--text)", fontSize: 13, fontWeight: 600 }}>Take Profit partiel (%)</div>
            <div style={{ color: "#555E6E", fontSize: 10, marginTop: 4 }}>Vend 50% de la position quand P&L ≥ +X%</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="number" min="1" step="1" value={tpInput} onChange={e => setTpInput(e.target.value)}
              style={{ width: 72, padding: "6px 10px", borderRadius: 6,
                background: "var(--input-bg)", border: "1px solid var(--text-dimmer)",
                color: "#00E5C0", fontSize: 13, textAlign: "right", outline: "none" }} />
            <span style={{ color: "#555E6E", fontSize: 11 }}>%</span>
            <button onClick={() => { const v = parseFloat(tpInput); if (v > 0) update("take_profit_pct", v); }}
              style={{ padding: "6px 12px", borderRadius: 6, border: "none",
                background: "#00E5C020", color: "#00E5C0", fontSize: 10, cursor: "pointer", fontWeight: 600 }}>
              Appliquer</button>
          </div>
        </div>

        {/* Max positions simultanées */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 20px", borderBottom: "1px solid var(--border2)",
        }}>
          <div>
            <div style={{ color: "var(--text)", fontSize: 13, fontWeight: 600 }}>Positions max simultanées</div>
            <div style={{ color: "#555E6E", fontSize: 10, marginTop: 4 }}>Bloque les nouveaux achats au-delà de ce seuil</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="number" min="1" max="20" step="1" value={maxPosInput} onChange={e => setMaxPosInput(e.target.value)}
              style={{ width: 72, padding: "6px 10px", borderRadius: 6,
                background: "var(--input-bg)", border: "1px solid var(--text-dimmer)",
                color: "#E0E4F0", fontSize: 13, textAlign: "right", outline: "none" }} />
            <span style={{ color: "#555E6E", fontSize: 11 }}>pos.</span>
            <button onClick={() => { const v = parseInt(maxPosInput); if (v > 0) update("max_positions", v); }}
              style={{ padding: "6px 12px", borderRadius: 6, border: "none",
                background: "#E0E4F022", color: "#E0E4F0", fontSize: 10, cursor: "pointer", fontWeight: 600 }}>
              Appliquer</button>
          </div>
        </div>

        {cfg.auto_buy_enabled && (
          <div style={{
            margin: "0 20px 16px", padding: "10px 14px",
            background: "#E0303011", border: "1px solid #E0303044",
            borderRadius: 6, fontSize: 10, color: "#E03030",
          }}>
            ⚠ Mode réel activé — les signaux BUY déclencheront des achats réels sur DEX.
            Assurez-vous que le wallet et les paramètres de risque sont correctement configurés.
          </div>
        )}

        {/* Section apparence */}
        <div style={{ padding: "12px 20px", background: "var(--bg3)",
                      fontSize: 8, color: "var(--text-dimmer)", letterSpacing: 2 }}>
          APPARENCE
        </div>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 20px", borderBottom: "1px solid var(--border2)",
        }}>
          <div>
            <div style={{ color: "#E0E4F0", fontSize: 13, fontWeight: 600 }}>Thème</div>
            <div style={{ color: "#555E6E", fontSize: 10, marginTop: 4 }}>
              {theme === "light" ? "Mode clair activé" : "Mode sombre activé"}
            </div>
          </div>
          <button
            onClick={onToggleTheme}
            style={{
              padding: "7px 18px", borderRadius: 6,
              background: theme === "light" ? "#1C2030" : "#E0E4F022",
              border: `1px solid ${theme === "light" ? "#2A3040" : "#E0E4F033"}`,
              color: "#E0E4F0", fontSize: 11, cursor: "pointer",
              fontFamily: "'IBM Plex Mono',monospace", letterSpacing: 1,
            }}
          >
            {theme === "light" ? "☽  SOMBRE" : "☀  CLAIR"}
          </button>
        </div>

        {/* Section wallet */}
        <div style={{ padding: "12px 20px", background: "var(--bg3)",
                      fontSize: 8, color: "var(--text-dimmer)", letterSpacing: 2 }}>
          WALLET BSC
        </div>
        {wallet && !wallet.error ? (
          <div style={{ padding: "16px 20px", display: "flex", gap: 32, alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 4 }}>USDT</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#E0E4F0" }}>${wallet.usdt?.toFixed(2) ?? "—"}</div>
            </div>
            <div>
              <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 4 }}>BNB</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#F0B90B" }}>{wallet.bnb?.toFixed(4) ?? "—"}</div>
            </div>
            {wallet.wallet && (
              <div style={{ marginLeft: "auto" }}>
                <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 4 }}>ADRESSE</div>
                <div style={{ fontSize: 9, color: "#555E6E", fontFamily: "'IBM Plex Mono',monospace" }}>
                  {wallet.wallet.slice(0, 8)}…{wallet.wallet.slice(-6)}
                </div>
              </div>
            )}
          </div>
        ) : wallet?.error ? (
          <div style={{ padding: "14px 20px", fontSize: 10, color: "#555E6E" }}>{wallet.error}</div>
        ) : (
          <div style={{ padding: "14px 20px", fontSize: 10, color: "#2A3040" }}>Chargement wallet...</div>
        )}
      </div>
    </div>
  );
}
